import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createCompany, listCompanies, deleteCompany, type Company } from "../../services/devioz";

export default function Companies() {
  const [items, setItems] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", domain: "", slug: "" });
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    setMsg(null);
    try {
      const res = await listCompanies({ page: 1, pageSize: 200 });
      setItems(res.data ?? []);
    } catch (err: any) {
      setError(err?.message ?? "Error cargando empresas");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    document.title = "Empresas • Devioz";
    void load();
  }, []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMsg(null);

    if (!form.name || !form.domain || !form.slug) {
      setError("Nombre, dominio y slug son obligatorios");
      return;
    }

    try {
      setLoading(true);
      await createCompany(form);
      setForm({ name: "", domain: "", slug: "" });
      setMsg("Empresa creada correctamente.");
      await load();
    } catch (err: any) {
      setError(err?.message || "No se pudo crear la empresa");
    } finally {
      setLoading(false);
    }
  }

  async function onDelete(c: Company) {
    if (!window.confirm(`¿Eliminar la empresa "${c.name}"?`)) return;

    setError(null);
    setMsg(null);
    try {
      setLoading(true);
      await deleteCompany(c.id);
      setMsg(`Empresa "${c.name}" eliminada correctamente.`);
      await load();
    } catch (err: any) {
      setError(err?.message || "No se pudo eliminar la empresa");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-fluid py-4">
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <Link to="/devioz">Home</Link>
        </li>
        <li className="breadcrumb-item">Devioz</li>
        <li className="breadcrumb-item active">Empresas</li>
      </ol>

      <h1 className="page-header mb-3">Empresas</h1>

      {(error || msg) && (
        <div className={`alert ${error ? "alert-danger" : "alert-success"}`}>
          {error || msg}
        </div>
      )}

      <div className="card border-0 mb-4 shadow-sm">
        <div className="card-body">
          <h5 className="card-title mb-3">Nueva empresa</h5>
          <form className="row g-3" onSubmit={onCreate} noValidate>
            <div className="col-md-4">
              <label className="form-label">Nombre *</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                disabled={loading}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Dominio (correo) *</label>
              <input
                className="form-control"
                placeholder="bcp.com"
                value={form.domain}
                onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                disabled={loading}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Slug *</label>
              <input
                className="form-control"
                placeholder="identificador-unico"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                disabled={loading}
              />
            </div>
            <div className="col-12">
              <button className="btn btn-primary" disabled={loading}>
                Crear
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <b className="d-block mb-3">Listado</b>
          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>N°</th>
                  <th>Nombre</th>
                  <th>Dominio</th>
                  <th>Slug</th>
                  <th>Estado</th>
                  <th>Creada</th>
                  <th className="text-center" style={{ width: 90 }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {!items.length && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      {loading ? "Cargando…" : "Sin empresas"}
                    </td>
                  </tr>
                )}
                {items.map((c, idx) => (
                  <tr key={c.id}>
                    <td>{idx + 1}</td>
                    <td>{c.name}</td>
                    <td>{c.domain}</td>
                    <td>{c.slug}</td>
                    <td>{c.status}</td>
                    <td>{c.created_at ? new Date(c.created_at).toLocaleString() : "-"}</td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => onDelete(c)}
                        title="Eliminar empresa"
                        disabled={loading}
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
